import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import logo from "../../assets/img/logo/green.png";
import { motion, AnimatePresence } from "framer-motion";
import { useUserStore } from "../../store/userStore";
import {
  clearStorage,
  deleteStorageItem,
  getStorageItem,
  setStorageItem,
} from "../../utils/sessionStorage";
import BannerTop from "../banner-top";
import { useUserChoiceStore } from "../../store/userStore";

import { User, LogOut, ChevronDown, Calendar } from "lucide-react";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearUserChoice } = useUserChoiceStore();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [toggle, setIsToggle] = useState(false);
  const [showSignUpSignIn, setShowSignUpSignIn] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const todayLoggedInTime = new Date().toISOString();
  const lastLoggedInTime = getStorageItem("lastLoggedIn");
  const user = useUserStore((state) => state.user);
  const isAboutService = location.pathname === "/about-service";
  const signInUrl =
    global.config.ROOTURL.prod === "https://lastminutewellness.ca/api"
      ? "https://my.lastminutewellness.ca/index.php?route=account/login"
      : "https://demo.lastminutewellness.ca/";

  const navbarItems = [
    {
      to: "/serviceList",
      name: "Book Now",
    },
    {
      to: "/about-us",
      name: "About us",
    },
    {
      to: "/contact",
      name: "Contact us",
    },
    {
      to: "/faq",
      name: "FAQ",
    },
  ];

  const handleLogout = () => {
    clearStorage();
    clearUserChoice();
    navigate("/create-account");
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!lastLoggedInTime) {
      setShowSignUpSignIn(true);
      return;
    }
    const lastLoginTime = new Date(lastLoggedInTime);
    const now = new Date();

    const diffInMs = now.getTime() - lastLoginTime.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays < 7) {
      setStorageItem("lastLoggedIn", todayLoggedInTime);
    }
    setShowSignUpSignIn(false);
  }, [lastLoggedInTime]);

  return (
    <div className="w-full sticky top-0 bg-white text-gray-900 shadow-md z-50">
      {/* <BannerTop /> */}
      <div className="mx-5">
        <nav className="h-[74px] flex items-center justify-between">
          {/* Left: Logo + Nav Links */}
          <div className="flex items-center gap-10 min-w-0">
            <Link to="/" className="sm:ml-8 flex-shrink-0">
              <img src={logo} alt="Logo" className="h-20 w-auto" />
            </Link>

            <ul className="hidden lg:flex items-center gap-8 text-gray-700 text-xl">
              {navbarItems.map((item: any) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="px-3 py-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Profile or Auth Buttons (Desktop) */}
          <div className="hidden lg:block relative">
            {/* {!showSignUpSignIn ? (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="group relative flex items-center gap-2 p-1.5 pr-4 rounded-full bg-white border border-slate-200 transition-all duration-300 hover:border-emerald-500/30 hover:shadow-md active:scale-95"
              >
                <div className="relative">
                  <img
                    src={`https://ui-avatars.com/api/?name=${user?.firstName || "User"}&background=0f172a&color=fff&bold=true`}
                    alt="Profile"
                    className="h-8 w-8 rounded-full border border-slate-100 shadow-sm"
                  />
                </div>
                
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform duration-500 ${isMenuOpen ? "rotate-180 text-emerald-600" : ""}`}
                />
              </button>
            ) : (
              <Link
                to={isAboutService ? signInUrl : "/about-service"}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-teal-500 text-white font-semibold shadow-sm hover:bg-teal-600 transition"
              >
                {isAboutService ? "Sign in" : "Join as a Provider"}
              </Link>
            )} */}

            <AnimatePresence>
              {isMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsMenuOpen(false)}
                  />

                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_20px_40px_rgba(15,23,42,0.08)] z-20"
                  >
                    {/* User Info Header */}
                    <div className="bg-slate-100 pt-4 pb-2 px-3 border-b border-slate-100">
                      <p className="text-md font-semibold text-slate-900 tracking-tight leading-none">
                        {user?.firstName || "Guest"}
                      </p>
                      <p className="text-base font-medium text-slate-500 mt-1.5 truncate">
                        {user?.email || "user@example.com"}
                      </p>
                    </div>

                    <div className="p-2">
                      {[
                        {
                          label: "My Profile",
                          icon: <User size={16} />,
                          to: "/user/profile",
                        },
                        {
                          label: "My Bookings",
                          icon: <Calendar size={16} />,
                          to: "/user/bookings",
                        },
                      ].map((link, idx) => (
                        <Link
                          key={idx}
                          to={link.to}
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-3 rounded-xl px-4 py-3 text-[14px] font-semibold text-slate-600 transition-all hover:bg-emerald-50 hover:text-emerald-700 group/item"
                        >
                          <span className="text-slate-400 group-hover/item:text-emerald-500 transition-colors">
                            {link.icon}
                          </span>
                          {link.label}
                        </Link>
                      ))}

                      {/* Logout Trigger - Styled specifically to look clean */}
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          setShowLogoutModal(true);
                        }}
                        className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-[14px] font-semibold text-slate-600 transition-all hover:bg-rose-50 hover:text-rose-600 group/logout"
                      >
                        <LogOut
                          size={16}
                          className="text-slate-400 group-hover/logout:text-rose-500"
                        />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-2xl"
            onClick={() => setIsToggle(!toggle)}
          >
            {toggle ? "✕" : "☰"}
          </button>
        </nav>
        <AnimatePresence>
          {showLogoutModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
              {/* Enhanced Backdrop with Blur */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowLogoutModal(false)}
                className="absolute inset-0 bg-slate-900/60"
              />

              {/* Responsive Modal Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-[90%] sm:max-w-sm overflow-hidden rounded-[24px] sm:rounded-[32px] bg-white p-6 sm:p-10 shadow-2xl text-center"
              >
                {/* Dynamic Icon Size */}
                <div className="mx-auto mb-6 flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-red-50 text-rose-500">
                  <LogOut className="w-7 h-7 sm:w-9 sm:h-9" />
                </div>

                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                  Are you sure?
                </h3>
                <p className="mt-3 text-[14px] sm:text-[15px] font-medium text-slate-500 leading-relaxed px-2">
                  You are about to end your current session. You'll need to sign
                  back in to access your bookings.
                </p>

                {/* Buttons: Stacked on mobile for better thumb-reach */}
                <div className="mt-8 flex flex-col gap-3">
                  <button
                    onClick={handleLogout} // Replace with your actual logout function
                    className="w-full rounded-2xl py-4 text-[15px] text-slate-700 font-semibold transition-all hover:bg-red-600  active:scale-[0.98] hover:text-white"
                  >
                    Yes, Log me out
                  </button>

                  <button
                    onClick={() => setShowLogoutModal(false)}
                    className="w-full rounded-2xl py-4 text-[15px] text-white font-semibold bg-black transition-all active:scale-[0.98]"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {toggle && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="lg:hidden absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 z-40 overflow-hidden"
          >
            <ul className="flex flex-col p-6 space-y-1">
              {/* 1. Primary Navigation Links */}
              {!showSignUpSignIn && (
                <div>
                  <li>
                    <Link
                      to="/user/profile"
                      className="flex items-center gap-4 px-4 py-3 text-[15px] font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-2xl transition-all"
                      onClick={() => setIsToggle(false)}
                    >
                      <span>Profile</span>
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/user/bookings"
                      className="flex items-center gap-4 px-4 py-3 text-[15px] font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-2xl transition-all"
                      onClick={() => setIsToggle(false)}
                    >
                      <span>My Bookings</span>
                    </Link>
                  </li>
                </div>
              )}
              {navbarItems.map((item: any) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="flex items-center px-4 py-3 text-[15px] font-semibold text-slate-700 hover:bg-slate-50 rounded-2xl transition-all active:scale-[0.98]"
                    onClick={() => setIsToggle(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}

              {/* 2. Authenticated User Section mobile section code */}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
